import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../../contexts/AuthContext';
import { ShellRail, useShellUI } from '../../shell/AppShell';
import { rateMyTradeAPI } from '../../utils/api';
import { ChevronRightIcon } from '../../shell/icons';
import TradeSides from './TradeSides';
import { readVotes, recordVote, sideLabels, votePct } from './rmtShared';

/**
 * RateMyTradeHome — the in-shell /rate-my-trade feed (public; anonymous
 * voting works). Rebuilt per the shell doctrine; voting logic carried from
 * the legacy feed (yes/no counts, one vote per browser).
 */

const RateMyTradeHome = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useShellUI();

  const [trades, setTrades] = useState(null);
  const [mine, setMine] = useState([]);
  const [votes, setVotes] = useState(readVotes);

  useEffect(() => {
    let active = true;
    rateMyTradeAPI
      .list({ limit: 20 })
      .then((res) => { if (active) setTrades(Array.isArray(res.data) ? res.data : res.data?.trades || []); })
      .catch(() => { if (active) setTrades([]); });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (!user) return undefined;
    let active = true;
    rateMyTradeAPI
      .mine()
      .then((res) => { if (active) setMine(Array.isArray(res.data) ? res.data : res.data?.trades || []); })
      .catch(() => {});
    return () => { active = false; };
  }, [user]);

  const castVote = async (trade, vote) => {
    if (votes[trade.shareId]) return;
    setVotes(recordVote(trade.shareId, vote));
    setTrades((prev) => prev.map((t) => (
      t.shareId === trade.shareId
        ? { ...t, yesCount: Number(t.yesCount || 0) + (vote === 'yes' ? 1 : 0), noCount: Number(t.noCount || 0) + (vote === 'no' ? 1 : 0) }
        : t
    )));
    try {
      await rateMyTradeAPI.vote(trade.shareId, vote);
    } catch {
      toast('Vote did not land — try again');
    }
  };

  const trending = (trades || [])
    .slice()
    .sort((a, b) => (Number(b.yesCount || 0) + Number(b.noCount || 0)) - (Number(a.yesCount || 0) + Number(a.noCount || 0)))
    .slice(0, 3);

  return (
    <div className="ft-view" style={{ maxWidth: 860 }}>
      <Helmet>
        <title>Rate My Trade — Fantasy Taco</title>
        <meta name="description" content="Share any dynasty trade and let the internet decide. Community votes in minutes." />
      </Helmet>

      <ShellRail>
        {user && mine.length > 0 && (
          <div className="ft-railsec">
            <span className="ft-k">Your trades on the ballot</span>
            {mine.slice(0, 4).map((t) => {
              const { total } = votePct(t);
              return (
                <button key={t.shareId} className="ft-railaction" onClick={() => navigate(`/rate-my-trade/${t.shareId}`)}>
                  <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {t.leagueName || 'Trade'} · {total} vote{total === 1 ? '' : 's'}
                  </span>
                  <ChevronRightIcon />
                </button>
              );
            })}
          </div>
        )}
        {trending.length > 0 && (
          <div className="ft-railsec">
            <span className="ft-k">Trending</span>
            {trending.map((t) => {
              const { total, send } = votePct(t);
              return (
                <div key={t.shareId} className="ft-acty" style={{ cursor: 'pointer' }} onClick={() => navigate(`/rate-my-trade/${t.shareId}`)}>
                  <span><b>{t.leagueName || 'Trade'}</b> · {total} votes</span>
                  <span className="ft-when">{send}% send</span>
                </div>
              );
            })}
          </div>
        )}
        <div className="ft-railsec">
          <span className="ft-k">How it works</span>
          <p className="ft-sub" style={{ fontSize: 13.5, marginTop: 8 }}>
            Share any trade, real or hypothetical. The link goes to your league chat. The votes settle the argument.
          </p>
        </div>
      </ShellRail>

      <span className="ft-k brand">Rate My Trade</span>
      <h1 className="ft-hero">Let the internet decide.</h1>
      <p className="ft-sub">Share any dynasty trade and the votes roll in.</p>
      <div style={{ marginTop: 22 }}>
        <button className="ft-btn" onClick={() => navigate('/rate-my-trade/create')}>Share a trade</button>
      </div>

      <div className="ft-section">
        {trades === null ? (
          <div className="ft-loading">Pulling the ballot…</div>
        ) : trades.length === 0 ? (
          <p className="ft-sub">Nothing on the ballot yet. Share the first trade.</p>
        ) : (
          trades.map((trade) => {
            const [labelA, labelB] = sideLabels(trade);
            const myVote = votes[trade.shareId];
            const { total, send } = votePct(trade);
            return (
              <div
                key={trade.shareId}
                className="ft-card"
                role="button"
                tabIndex={0}
                style={{ marginBottom: 14, maxWidth: 720, cursor: 'pointer', transition: 'all .3s cubic-bezier(0.16, 1, 0.3, 1)' }}
                onClick={() => navigate(`/rate-my-trade/${trade.shareId}`)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && e.target === e.currentTarget) navigate(`/rate-my-trade/${trade.shareId}`);
                }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                  <span style={{ fontSize: 14.5, fontWeight: 600 }}>
                    Should {labelA} do this?
                  </span>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <span className="ft-chip outline">{trade.leagueName || 'Dynasty league'}</span>
                    <ChevronRightIcon style={{ width: 14, height: 14, color: 'var(--ft-ink-3)', flex: 'none' }} />
                  </span>
                </div>
                <TradeSides
                  trade={trade}
                  teamA={trade.cachedTeamData?.teamA}
                  teamB={trade.cachedTeamData?.teamB}
                  labelA={`${labelA} receives`}
                  labelB={`${labelB} receives`}
                />
                <div style={{ height: 8, borderRadius: 980, background: '#EBEBED', overflow: 'hidden', marginTop: 16 }}>
                  <div style={{ width: `${total ? send : 0}%`, height: '100%', background: 'var(--ft-green)', borderRadius: 980, transition: 'width .5s var(--ft-ease)' }} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--ft-ink-3)', marginTop: 7 }}>
                  <span>{total ? `Send it — ${send}%` : 'No votes yet'}</span>
                  <span>{total} vote{total === 1 ? '' : 's'}{myVote ? ' · you voted' : ''}</span>
                </div>
                <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
                  <button className="ft-btn sm" disabled={!!myVote} onClick={(e) => { e.stopPropagation(); castVote(trade, 'yes'); }}>
                    {myVote === 'yes' ? 'You said send it' : 'Send it'}
                  </button>
                  <button className="ft-btn sm ghost" disabled={!!myVote} onClick={(e) => { e.stopPropagation(); castVote(trade, 'no'); }}>
                    {myVote === 'no' ? 'You passed' : 'Pass'}
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default RateMyTradeHome;
